/* eslint-disable max-lines -- one route table: the partner app routes */
import type { BookingMessageTemplate, CapacityContributionLocationType } from '@navio/nsdk';
import type { WorkingHoursShowOption } from '../capacity/workingHoursShow';
import { InvoiceTab, OrganizationTab, TireHotelDetailTab } from './adminRouteParams';
import {
  BookingDetailTab,
  CampaignDetailTab,
  CAR_USER_GROUP_QUERY_PARAM,
  CarTab,
  ChannelView,
  CouponDetailTab,
  CustomerDetailTab,
  FleetScheduleDetailTab,
  GarageDetailTab,
  MembershipDiscountDetailTab,
  MembershipProgramDetailTab,
  ProductsCatalogTab,
  PromotionsTab,
  RecallsStatusTab,
  ReferralProgramDetailTab,
  ROUTE_DETAILS_QUERY_PARAM,
  ROUTE_STATUS_GROUP_QUERY_PARAMS,
  RoutePlanningTab,
  RouteStatusTab,
  SalesItemTab,
  ScheduleView,
  StorageFacilityTab,
  TaskDetailTab,
  TaskTab,
  TaskTemplateDetailTab,
  TireOfferTab,
  WebhookEndpointScope,
  WmsWarehouseTab,
  WorkerContractTab,
  WorkerDetailTab,
  WorkerShiftTab,
  WorkerTimeTrackingSettingsTab,
  WorkerTimeTrackingTab
} from './partnerRouteParams';
import { defineRoutes } from './types';
import { type FilterSearch, getUrlWithQueryParams } from './utils';

// Define the parameters for the routes

type StringOrNumber = string | number;

type PathConfig = {
  parkingPlaceDetail: { id: StringOrNumber };
  siteGroupDetail: { id: StringOrNumber };
  siteDetail: { id: StringOrNumber };
  siteCreate: { 'group-id'?: number } | undefined;
  createGarage: { 'site-id'?: number } | undefined;
  createStorageFacility: { 'site-id'?: number } | undefined;
  wmsWarehouseCreate: { 'site-id'?: number } | undefined;
  forecastOverview: void;
  revenue: void;
  forecastGrowth: void;
  forecastVolume: void;
  forecastCapacity: void;
  forecastScenarios: void;
  forecastSeasons: void;
  /** One worker shift: its hours, breaks and note, with the bookable time inside it. */
  workerShiftDetails: { workerShiftId: StringOrNumber; tab?: WorkerShiftTab };
  /** Resolves a capacity contribution to its worker shift, or to expected staffing. Old links land here. */
  bookableTimeDetails: { capacityContributionId: StringOrNumber };
  expectedStaffingDetails: {
    capacityContributionId: StringOrNumber;
    tab?: WorkerShiftTab;
  };
  workerShiftsOverviewDay: {
    date: string;
    'wh-view'?: 'month';
    /** The capability the board was narrowed to. */
    'wh-service'?: number;
    'wh-transfer-orders'?: boolean;
  };
  route: { id: StringOrNumber };
  routeMessage: {
    id: StringOrNumber;
    /** Preselected; without them, the stops not visited yet are. */
    bookingIds?: number[];
    /** Suggested first in the templates. */
    template?: BookingMessageTemplate;
  };
  routeDiscount: {
    id: StringOrNumber;
    /** Preselected; without them, the stops not visited yet are. */
    bookingIds?: number[];
  };
  routeSettlementDetails: { routeSettlementId: StringOrNumber };
  routeProblemDetails: { problemId: StringOrNumber };
  routeSolutionDetails: {
    problemId: StringOrNumber;
    solutionId: StringOrNumber;
  };
  workerContractAddress: {
    workerContractId: StringOrNumber;
    addressPairId: StringOrNumber;
  };
  serviceDepartmentArea: { id: StringOrNumber };
  salesItemDetails: { id: StringOrNumber; tab?: SalesItemTab };
  salesItemTaskChangelog: { id: StringOrNumber };
  editWorkerContract: {
    workerContractId: StringOrNumber;
    tab?: WorkerContractTab;
  };
  bookingDetailsDirect: { bookingId: StringOrNumber; tab?: BookingDetailTab };
  bookingSendSms: { bookingId: StringOrNumber };
  invoiceDetail: { invoiceId: StringOrNumber; tab?: InvoiceTab };
  invoiceCreate: { userGroupId?: StringOrNumber };
  bookingTaskDetails: {
    bookingId: StringOrNumber;
    taskId: StringOrNumber;
    tab?: TaskDetailTab;
  };
  smsDetails: { smsId: StringOrNumber };
  editUserGroup: {
    id: StringOrNumber;
    tab?: CustomerDetailTab;
    'tire-hotel-id'?: StringOrNumber;
  };
  newBookingUserGroup: { id: StringOrNumber };
  createPricingAgreements: { id: StringOrNumber };
  carDetail: {
    carId: StringOrNumber;
    tab?: CarTab;
    userGroupId?: StringOrNumber;
  };
  carWheelSetDetail: {
    carId: StringOrNumber;
    wheelSetId: StringOrNumber;
    userGroupId?: StringOrNumber;
  };
  carChangeOwner: { carId: StringOrNumber; userGroupId?: StringOrNumber };
  storedItemDetail: { identifier: string };
  order: { orderId: StringOrNumber };
  refundOrderLines: { orderId: StringOrNumber };
  shopOrderDetails: { orderId: StringOrNumber };
  editPricingAgreement: {
    userGroupId: StringOrNumber;
    priceAgreementId: StringOrNumber;
  };
  createWebhook: { scope?: WebhookEndpointScope };
  webhookDetails: {
    webhookEndpointId: StringOrNumber;
    scope?: WebhookEndpointScope;
  };
  webhookEventDetails: {
    webhookEndpointId: StringOrNumber;
    webhookEventId: StringOrNumber;
    scope?: WebhookEndpointScope;
  };
  webhookAttemptDetails: {
    webhookEndpointId: StringOrNumber;
    webhookAttemptId: StringOrNumber;
    scope?: WebhookEndpointScope;
  };
  serviceWorker: { id: StringOrNumber; tab?: WorkerDetailTab };
  /** With a route, the SMS is about it: the route's department sends it, and it shows on the route. */
  workerSendMessage: { id: StringOrNumber; routeId?: number };
  workerAbsence: { id: StringOrNumber };
  workerTimeTracking: { tab?: WorkerTimeTrackingTab };
  workerTimeTrackingSettings: {
    tab?: WorkerTimeTrackingSettingsTab;
    'service-department'?: number;
  };
  workerHourlyBonusPolicy: { id: StringOrNumber };
  workerTimeLog: { id: StringOrNumber };
  workerPayroll: { id: StringOrNumber };
  createWorkerPayroll: void;
  tireOfferLead: { carId: StringOrNumber; userGroupId: StringOrNumber; season: StringOrNumber };
  tireStorageOverview: { 'not-moved-days'?: number };
  tireStorageIssue: {
    'car-id': number;
    'user-group-id': number;
    reason: string;
    'not-moved-days'?: number;
  };
  tireSetOffer: { id: StringOrNumber; tab?: TireOfferTab };
  tireHotelLocationEdit: { id: StringOrNumber };
  tireHotelDetail: { id: StringOrNumber; tab?: TireHotelDetailTab };
  editStorageFacility: { id: StringOrNumber; tab?: StorageFacilityTab };
  storageArea: { id: StringOrNumber };
  storageUnit: { id: StringOrNumber };
  storageFacilityTireHotelImport: { id: StringOrNumber };
  garagePickingList: {
    'storage-facility-id'?: number;
    'from-date'?: string;
    'to-date'?: string;
  };
  garagePickingListDetail: {
    identifier: string;
    storageFacilityId?: number;
    'from-date'?: string;
    'to-date'?: string;
  };
  bookingProposal: { id: StringOrNumber };
  recall: { id: StringOrNumber };
  recalls: { status?: RecallsStatusTab };
  recallsSendReminderJob: { serviceDepartmentId: number; jobId: string };
  editUnableToCompleteReason: { id: StringOrNumber };
  promotions: { tab?: PromotionsTab };
  campaignDetail: { id: StringOrNumber; tab?: CampaignDetailTab };
  referralProgramDetail: {
    brandId: StringOrNumber;
    tab?: ReferralProgramDetailTab;
  };
  couponDetail: { id: StringOrNumber; tab?: CouponDetailTab };
  membershipProgramDetail: {
    id: StringOrNumber;
    tab?: MembershipProgramDetailTab;
  };
  membershipDiscountDetail: {
    id: StringOrNumber;
    tab?: MembershipDiscountDetailTab;
  };
  couponCreate: { 'user-group'?: number };
  userGroupMemberDetails: {
    userGroupId: StringOrNumber;
    userId: StringOrNumber;
    tab?: string;
  };
  leadGenerationDetail: { id: StringOrNumber };
  supportConversation: { id: StringOrNumber };
  equipmentItemDetail: { id: StringOrNumber };
  equipmentTemplateDetail: { id: StringOrNumber };
  equipmentKitDetail: { id: StringOrNumber };
  equipmentTransferDetail: { id: StringOrNumber };
  equipmentAuditDetail: { id: StringOrNumber };
  equipmentStockAlertDetail: { id: StringOrNumber };
  equipmentPurchasePlanDetail: { id: StringOrNumber };
  equipmentVendorDetail: { id: StringOrNumber };
  unlockLocks: { 'storage-facility-id'?: number };
  unlockLockDetail: { id: StringOrNumber; 'storage-facility-id'?: number };
  unlockAccessEvents: { 'storage-facility-id'?: number };
  wmsWarehouseDetail: { id: StringOrNumber; tab?: WmsWarehouseTab };
  /** WMS lists take their filters (`filterSearch`); create and scan forms read `warehouse-id`. */
  wmsLevels: { filters?: FilterSearch };
  wmsLevelDetail: { id: StringOrNumber };
  wmsReservations: { filters?: FilterSearch };
  wmsReservationDetail: { id: StringOrNumber };
  wmsAllocations: { filters?: FilterSearch };
  wmsAllocationDetail: { id: StringOrNumber };
  wmsMovements: { filters?: FilterSearch };
  wmsAvailability: { 'warehouse-id'?: number };
  wmsCycleCounts: { filters?: FilterSearch };
  wmsCycleCountCreate: { 'warehouse-id'?: number };
  wmsCycleCountDetail: { id: StringOrNumber };
  wmsStockPolicies: { filters?: FilterSearch };
  wmsTransferCreate: { 'warehouse-id'?: number };
  wmsTransferDetail: { id: StringOrNumber };
  wmsScan: { 'warehouse-id'?: number };
  editTireInventoryConfiguration: { brandId: StringOrNumber };
  editTask: { id: StringOrNumber; tab?: TaskDetailTab };
  createTaskTemplate: void;
  editTaskTemplate: { id: StringOrNumber; tab?: TaskTemplateDetailTab };
  ruleSet: { id: StringOrNumber };
  editTag: { id: StringOrNumber };
  editGarage: { id: StringOrNumber; tab?: GarageDetailTab };
  fleetScheduleDetail: { id: StringOrNumber; tab?: FleetScheduleDetailTab };
  fleetScheduleVehicleDetail: { id: StringOrNumber };
  fleetEditOrganization: { id: StringOrNumber; tab?: OrganizationTab };
  fleetEditDepartment: { id: StringOrNumber };
  fleetDepartment: {
    organizationId: StringOrNumber;
    departmentId: StringOrNumber;
  };
  garageSchedule: { 'garage-id'?: number; date?: string; view?: ScheduleView };
  calendar: { channel?: ChannelView };
  mobileCoverage: {
    view?: 'coverage' | 'staffing' | 'week' | 'windows';
    from?: string;
    date?: string;
    'service-department'?: number;
  };
  mobileCoverageCategory: {
    categoryId: StringOrNumber;
    from?: string;
    date?: string;
    area?: number;
    'service-department'?: number;
  };
  mobileCoverageService: {
    salesItemId: StringOrNumber;
    from?: string;
    date?: string;
    area?: number;
    'service-department'?: number;
  };
  today: { channel?: ChannelView; 'garage-id'?: number };
  productsCatalog: { tab?: ProductsCatalogTab };
  createProduct: void;
  tasks: { tab?: TaskTab };
  routePlanning: {
    tab?: RoutePlanningTab;
    date?: string;
    'service-department-id'?: number;
  };
  bookingUpdateRequestDetail: { id: StringOrNumber };
  ticketDetail: { id: StringOrNumber; tab?: 'details' | 'activity' };
  emailDetail: { id: StringOrNumber };
  bookings: { filters?: FilterSearch };
  /** `search` is the list's free-text search; filters go through `filterSearch`. */
  tireHotels: { search?: string | null; filters?: FilterSearch };
  tireSetOffers: { filters?: FilterSearch };
  fleetCars: { filters?: FilterSearch };
  workerShifts: {
    'capacity-section'?: 'overview' | 'shifts' | 'baseline';
    /** The shift list's filters (`filterSearch`). */
    filters?: FilterSearch;
    'wh-anchor'?: string;
    'wh-day'?: string;
    /** The capability the board is narrowed to; absent counts every service. */
    'wh-service'?: number;
    /** What the board shows; absent shows everything. */
    'wh-show'?: WorkingHoursShowOption[];
    /** Count transfer-order work too; absent leaves it out. */
    'wh-transfer-orders'?: boolean;
    'wh-view'?: 'month' | 'week';
    'wh-worker'?: string;
    'wh-worker-id'?: number;
  };
  routeStatus: {
    date?: string;
    tab?: RouteStatusTab;
    /** Narrows the list to matching routes, and the map to matching stops: a worker name, customer or license plate. */
    search?: string;
    /** Opens this route's details sheet on arrival. Needs `expandGroups` so the row it hangs off is mounted. */
    routeDetailsId?: number;
    /** Opens all three status groups, so a link to one route lands on a visible row whatever was left collapsed. */
    expandGroups?: boolean;
  };
  routeStatusEdit: {
    date?: string;
    /** The route being worked on: shown first, and every move goes to or from it. */
    routeId?: StringOrNumber;
  };
  workerShiftsCreate: {
    date?: string;
    'location-type'?: CapacityContributionLocationType;
    'service-department-id'?: number;
    'service-worker-contract-id'?: number;
    'supply-kind'?: 'shift' | 'staffing';
    /** Adds bookable time to this shift only — its worker, day, hours, breaks and note stay; saving returns to it. */
    'worker-shift-id'?: number;
  };
  /** `absence-status` is the status chips' own param; the date range is a list filter (`filterSearch`). */
  workerAbsences: { 'absence-status'?: string; filters?: FilterSearch };
  storageCountSessionDetail: { id: StringOrNumber };
  transferOrderDetail: { id: StringOrNumber; tab?: string };
  transferOrderAddWheelSets: { id: StringOrNumber };
  transferOrderEmailDetail: {
    transferOrderId: StringOrNumber;
    emailId: StringOrNumber;
  };
  rehomeCreate: { fromTireHotelId?: number };
  rehomeDetail: { id: StringOrNumber };
  signageDashboardDetail: { id: StringOrNumber };
  signagePlaylistDetail: { id: StringOrNumber };
  signageDeviceDetail: { id: StringOrNumber };
  signagePair: { code?: string };
  inspectionSessionDetail: { id: StringOrNumber };
};

export const partnerRoutes = defineRoutes<PathConfig>()({
  forecastOverview: {
    getPath: () => '/forecast',
    getBasePath: () => '/forecast'
  },
  revenue: {
    getPath: () => '/revenue',
    getBasePath: () => '/revenue'
  },
  forecastGrowth: {
    getPath: () => '/forecast/growth',
    getBasePath: () => '/forecast/growth'
  },
  forecastVolume: {
    getPath: () => '/forecast/growth/volume',
    getBasePath: () => '/forecast/growth/volume'
  },
  forecastCapacity: {
    getPath: () => '/forecast/capacity',
    getBasePath: () => '/forecast/capacity'
  },
  forecastScenarios: {
    getPath: () => '/forecast/scenarios',
    getBasePath: () => '/forecast/scenarios'
  },
  forecastSeasons: {
    getPath: () => '/forecast/seasons',
    getBasePath: () => '/forecast/seasons'
  },

  editUnableToCompleteReason: {
    getPath: (data) => `/delivery/unable-to-complete-reasons/${data.id}/edit`,
    getBasePath: () => '/delivery/unable-to-complete-reasons/:id/edit'
  },
  createUnableToCompleteReason: {
    getPath: () => `/delivery/unable-to-complete-reasons/create`,
    getBasePath: () => '/delivery/unable-to-complete-reasons/create'
  },
  leadGeneration: {
    getPath: () => `/lead-generation`,
    getBasePath: () => '/lead-generation'
  },
  leadGenerationDetail: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/lead-generation/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/lead-generation/:id'
  },
  /**
   * Customer contact hub under partner `/support/*`.
   * Inbox/overview paths match admin; schedules stay admin-only until
   * department-scoped schedule APIs exist (admin uses `/support-schedules`).
   */
  supportInbox: {
    getPath: () => '/support/inbox',
    getBasePath: () => '/support/inbox'
  },
  supportConversation: {
    getPath: (data) => `/support/inbox/${data.id}`,
    getBasePath: () => '/support/inbox/:id'
  },
  supportOverview: {
    getPath: () => '/support/overview',
    getBasePath: () => '/support/overview'
  },
  recruitmentApplicants: {
    getPath: () => '/recruitment/applicants',
    getBasePath: () => '/recruitment/applicants'
  },
  recruitmentPositions: {
    getPath: () => '/recruitment/positions',
    getBasePath: () => '/recruitment/positions'
  },
  recruitmentPipeline: {
    getPath: () => '/recruitment/pipeline',
    getBasePath: () => '/recruitment/pipeline'
  },
  recallsOverview: {
    getPath: () => `/recalls/overview`,
    getBasePath: () => '/recalls/overview'
  },
  recallPrograms: {
    getPath: () => `/recalls/programs`,
    getBasePath: () => '/recalls/programs'
  },
  recallProgramTemplates: {
    getPath: () => `/recalls/programs/templates`,
    getBasePath: () => '/recalls/programs/templates'
  },
  /** Unified recalls work page. */
  recalls: {
    getPath: (data = {}) =>
      getUrlWithQueryParams({
        path: `/recalls`,
        queryParams: data.status ? { status: data.status } : undefined
      }),
    getBasePath: () => '/recalls'
  },
  recallsBackgroundJobs: {
    getPath: () => '/recalls/background-jobs',
    getBasePath: () => '/recalls/background-jobs'
  },
  recallsSendReminders: {
    getPath: () => '/recalls/send-reminders',
    getBasePath: () => '/recalls/send-reminders'
  },
  recallsSendReminderJob: {
    getPath: (data) => `/recalls/send-reminders/${data.serviceDepartmentId}/${data.jobId}`,
    getBasePath: () => '/recalls/send-reminders/:serviceDepartmentId/:jobId'
  },
  /** @deprecated Prefer `recalls` with `status=draft`. Kept for redirects. */
  // oxlint-disable-next-line typescript/no-deprecated -- legacy URL kept as a redirect source
  recallsPublish: {
    getPath: () => `/recalls/publish`,
    getBasePath: () => '/recalls/publish'
  },
  /** @deprecated Prefer `recalls` with `status=pending`. Kept for redirects. */
  // oxlint-disable-next-line typescript/no-deprecated -- legacy URL kept as a redirect source
  recallsPending: {
    getPath: () => `/recalls/pending`,
    getBasePath: () => '/recalls/pending'
  },
  /** @deprecated Prefer `recalls` with `status=history`. Kept for redirects. */
  // oxlint-disable-next-line typescript/no-deprecated -- legacy URL kept as a redirect source
  recallsHistory: {
    getPath: () => `/recalls/history`,
    getBasePath: () => '/recalls/history'
  },
  /** @deprecated Prefer `recalls` with `status=history`. Kept for redirects. */
  // oxlint-disable-next-line typescript/no-deprecated -- legacy URL kept as a redirect source
  recallsAll: {
    getPath: () => `/recalls/all`,
    getBasePath: () => '/recalls/all'
  },
  recall: {
    getPath: (data) => `/recalls/${data.id}`,
    getBasePath: () => '/recalls/:id'
  },
  bookingProposals: {
    getPath: () => `/booking-proposals`,
    getBasePath: () => '/booking-proposals'
  },
  bookingProposal: {
    getPath: (data) => `/booking-proposals/${data.id}`,
    getBasePath: () => '/booking-proposals/:id'
  },
  storageFacilities: {
    getPath: () => `/storage-facilities`,
    getBasePath: () => '/storage-facilities'
  },
  storageCountSessionDetail: {
    getPath: ({ id }) => `/storage/count-sessions/${id}`,
    getBasePath: () => '/storage/count-sessions/:id'
  },
  createStorageFacility: {
    getPath: (data = {}) =>
      getUrlWithQueryParams({
        path: '/storage-facilities/create',
        queryParams: data
      }),
    getBasePath: () => '/storage-facilities/create'
  },
  editStorageFacility: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/storage-facilities/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/storage-facilities/:id'
  },
  storageArea: {
    getPath: (data) => `/storage-areas/${data.id}`,
    getBasePath: () => '/storage-areas/:id'
  },
  storageUnit: {
    getPath: (data) => `/storage-units/${data.id}`,
    getBasePath: () => '/storage-units/:id'
  },
  storageFacilityTireHotelImport: {
    getPath: (data) => `/storage-facilities/${data.id}/tire-hotel-import`,
    getBasePath: () => '/storage-facilities/:id/tire-hotel-import'
  },
  garagePickingList: {
    getPath: (data) =>
      getUrlWithQueryParams({
        path: '/garage-picking-list',
        queryParams: data
      }),
    getBasePath: () => '/garage-picking-list'
  },
  garagePickingListDetail: {
    getPath: ({ identifier, ...rest }) =>
      getUrlWithQueryParams({
        path: `/garage-picking-list/${identifier}`,
        queryParams: rest
      }),
    getBasePath: () => '/garage-picking-list/:identifier'
  },
  tireStoragePickups: {
    getPath: () => `/tire-hotels-pickups`,
    getBasePath: () => '/tire-hotels-pickups'
  },
  tireStorageOverview: {
    getPath: (queryParams = {}) => getUrlWithQueryParams({ path: `/tire-hotel/overview`, queryParams }),
    getBasePath: () => '/tire-hotel/overview'
  },
  tireStorageIssue: {
    getPath: (queryParams) =>
      getUrlWithQueryParams({
        path: `/tire-hotel/issues`,
        queryParams
      }),
    getBasePath: () => '/tire-hotel/issues'
  },
  tireStorageHistory: {
    getPath: () => `/tire-storage-history`,
    getBasePath: () => '/tire-storage-history'
  },
  tireHotelLocations: {
    getPath: () => `/tire-hotels-competitors`,
    getBasePath: () => '/tire-hotels-competitors'
  },
  tireHotelLocationCreate: {
    getPath: () => `/tire-hotels-competitors/create`,
    getBasePath: () => '/tire-hotels-competitors/create'
  },
  tireHotelLocationEdit: {
    getPath: (data) => `/tire-hotels-competitors/${data.id}`,
    getBasePath: () => '/tire-hotels-competitors/:id'
  },
  tireHotels: {
    getPath: (queryParams = {}) => getUrlWithQueryParams({ path: '/tire-hotels', queryParams }),
    getBasePath: () => '/tire-hotels'
  },
  tireHotelDetail: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/tire-hotels/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/tire-hotels/:id'
  },
  myAccount: {
    getPath: () => `/my-account`,
    getBasePath: () => '/my-account'
  },
  devSettings: {
    getPath: () => `/dev-settings`,
    getBasePath: () => '/dev-settings'
  },
  carDetail: {
    getPath: ({ carId, userGroupId, ...rest }) =>
      getUrlWithQueryParams({
        path: `/cars/${carId}`,
        queryParams: { ...rest, [CAR_USER_GROUP_QUERY_PARAM]: userGroupId }
      }),
    getBasePath: () => '/cars/:carId'
  },
  serviceWorkers: {
    getPath: () => `/workers`,
    getBasePath: () => '/workers'
  },
  serviceWorker: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/workers/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/workers/:id'
  },
  workerSendMessage: {
    getPath: ({ id, ...queryParams }) =>
      getUrlWithQueryParams({
        path: `/workers/${id}/send-message`,
        queryParams
      }),
    getBasePath: () => '/workers/:id/send-message'
  },
  createWorker: {
    getPath: () => `/workers/create`,
    getBasePath: () => '/workers/create'
  },
  workerAbsences: {
    getPath: (queryParams = {}) => getUrlWithQueryParams({ path: `/workers/absences`, queryParams }),
    getBasePath: () => '/workers/absences'
  },
  workerAbsence: {
    getPath: ({ id }) => `/workers/absences/${id}`,
    getBasePath: () => '/workers/absences/:id'
  },
  workerTimeTracking: {
    getPath: ({ tab } = {}) =>
      getUrlWithQueryParams({
        path: `/workers/time-tracking`,
        queryParams: { tab }
      }),
    getBasePath: () => '/workers/time-tracking'
  },
  workerTimeTrackingSettings: {
    getPath: (queryParams = {}) =>
      getUrlWithQueryParams({
        path: `/workers/time-tracking-settings`,
        queryParams
      }),
    getBasePath: () => '/workers/time-tracking-settings'
  },
  createWorkerHourlyBonusPolicy: {
    getPath: () => `/workers/time-tracking-settings/hourly-bonus-policies/new`,
    getBasePath: () => '/workers/time-tracking-settings/hourly-bonus-policies/new'
  },
  workerHourlyBonusPolicy: {
    getPath: ({ id }) => `/workers/time-tracking-settings/hourly-bonus-policies/${id}`,
    getBasePath: () => '/workers/time-tracking-settings/hourly-bonus-policies/:id'
  },
  workerTimeLog: {
    getPath: ({ id }) => `/workers/time-logs/${id}`,
    getBasePath: () => '/workers/time-logs/:id'
  },
  createCustomer: {
    getPath: () => `/customers/create`,
    getBasePath: () => '/customers/create'
  },
  editPricingAgreement: {
    getPath: (data) => `/customers/${data.userGroupId}/pricing-agreements/${data.priceAgreementId}`,
    getBasePath: () => '/customers/:userGroupId/pricing-agreements/:priceAgreementId'
  },
  carChangeOwner: {
    getPath: ({ carId, userGroupId }) =>
      getUrlWithQueryParams({
        path: `/cars/${carId}/change-owner`,
        queryParams: { [CAR_USER_GROUP_QUERY_PARAM]: userGroupId }
      }),
    getBasePath: () => '/cars/:carId/change-owner'
  },
  carWheelSetDetail: {
    getPath: ({ carId, wheelSetId, userGroupId }) =>
      getUrlWithQueryParams({
        path: `/cars/${carId}/wheel-sets/${wheelSetId}`,
        queryParams: { [CAR_USER_GROUP_QUERY_PARAM]: userGroupId }
      }),
    getBasePath: () => '/cars/:carId/wheel-sets/:wheelSetId'
  },
  storedItems: {
    getPath: () => '/stored-items',
    getBasePath: () => '/stored-items'
  },
  storedItemDetail: {
    getPath: ({ identifier }) => `/stored-items/${identifier}`,
    getBasePath: () => '/stored-items/:identifier'
  },
  editUserGroup: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/customers/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/customers/:id'
  },
  userGroups: {
    getPath: () => `/customers`,
    getBasePath: () => '/customers'
  },
  promotions: {
    getPath: (data = {}) => getUrlWithQueryParams({ path: '/promotions', queryParams: data }),
    getBasePath: () => '/promotions'
  },
  promotionsNew: {
    getPath: () => `/promotions/new`,
    getBasePath: () => '/promotions/new'
  },
  campaigns: {
    getPath: () =>
      getUrlWithQueryParams({
        path: '/promotions',
        queryParams: { tab: PromotionsTab.CAMPAIGNS }
      }),
    getBasePath: () => '/campaigns'
  },
  campaignCreate: {
    getPath: () => `/promotions/campaigns/create`,
    getBasePath: () => '/promotions/campaigns/create'
  },
  campaignDetail: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/promotions/campaigns/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/promotions/campaigns/:id'
  },
  coupons: {
    getPath: () =>
      getUrlWithQueryParams({
        path: '/promotions',
        queryParams: { tab: PromotionsTab.COUPONS }
      }),
    getBasePath: () => '/coupons'
  },
  couponCreate: {
    getPath: (data) =>
      getUrlWithQueryParams({
        path: `/promotions/coupons/create`,
        queryParams: data ?? {}
      }),
    getBasePath: () => '/promotions/coupons/create'
  },
  couponDetail: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/promotions/coupons/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/promotions/coupons/:id'
  },
  memberships: {
    getPath: () =>
      getUrlWithQueryParams({
        path: '/promotions',
        queryParams: { tab: PromotionsTab.CAMPAIGNS }
      }),
    getBasePath: () => '/memberships'
  },
  membershipProgramCreate: {
    getPath: () => '/promotions/campaigns/create',
    getBasePath: () => '/promotions/membership-programs/create'
  },
  membershipProgramDetail: {
    getPath: () =>
      getUrlWithQueryParams({
        path: '/promotions',
        queryParams: { tab: PromotionsTab.CAMPAIGNS }
      }),
    getBasePath: () => '/promotions/membership-programs/:id'
  },
  membershipDiscountCreate: {
    getPath: () =>
      getUrlWithQueryParams({
        path: '/promotions',
        queryParams: { tab: PromotionsTab.CAMPAIGNS }
      }),
    getBasePath: () => '/promotions/memberships/discounts/create'
  },
  membershipDiscountDetail: {
    getPath: () =>
      getUrlWithQueryParams({
        path: '/promotions',
        queryParams: { tab: PromotionsTab.CAMPAIGNS }
      }),
    getBasePath: () => '/promotions/memberships/discounts/:id'
  },
  referrals: {
    getPath: () =>
      getUrlWithQueryParams({
        path: '/promotions',
        queryParams: { tab: PromotionsTab.REFERRAL }
      }),
    getBasePath: () => '/referrals'
  },
  referralProgramDetail: {
    getPath: ({ brandId, ...rest }) =>
      getUrlWithQueryParams({
        path: `/promotions/referral/${brandId}`,
        queryParams: rest
      }),
    getBasePath: () => '/promotions/referral/:brandId'
  },
  priceAgreements: {
    getPath: () => `/price-agreements`,
    getBasePath: () => '/price-agreements'
  },
  feedback: {
    getPath: () => `/customer-feedback`,
    getBasePath: () => `/customer-feedback`
  },
  bookings: {
    getPath: (queryParams = {}) => getUrlWithQueryParams({ path: '/bookings', queryParams }),
    getBasePath: () => '/bookings'
  },
  bookingCalendar: {
    getPath: () => `/booking-calendar`,
    getBasePath: () => '/booking-calendar'
  },
  bookingAnalytics: {
    getPath: () => `/booking-analytics`,
    getBasePath: () => '/booking-analytics'
  },
  bookingDetailsDirect: {
    getPath: ({ bookingId, ...rest }) =>
      getUrlWithQueryParams({
        path: `/bookings/${bookingId}`,
        queryParams: rest
      }),
    getBasePath: () => '/bookings/:bookingId'
  },
  bookingSendSms: {
    getPath: ({ bookingId }) => `/bookings/${bookingId}/send-sms`,
    getBasePath: () => '/bookings/:bookingId/send-sms'
  },
  bookingTaskDetails: {
    getPath: ({ bookingId, taskId, ...rest }) =>
      getUrlWithQueryParams({
        path: `/bookings/${bookingId}/tasks/${taskId}`,
        queryParams: rest
      }),
    getBasePath: () => '/bookings/:bookingId/tasks/:taskId'
  },
  smsDetails: {
    getPath: (data) => `/sms/${data.smsId}`,
    getBasePath: () => '/sms/:smsId'
  },
  advancedSettings: {
    getPath: () => `/settings/advanced`,
    getBasePath: () => '/settings/advanced'
  },
  createWebhook: {
    getPath: (data = {}) =>
      getUrlWithQueryParams({
        path: `/settings/advanced/webhooks/create`,
        queryParams:
          data.scope === WebhookEndpointScope.ORGANIZATION ? { scope: WebhookEndpointScope.ORGANIZATION } : {}
      }),
    getBasePath: () => '/settings/advanced/webhooks/create'
  },
  webhookDetails: {
    getPath: ({ webhookEndpointId, scope }) =>
      getUrlWithQueryParams({
        path: `/settings/advanced/webhooks/${webhookEndpointId}`,
        queryParams: scope === WebhookEndpointScope.ORGANIZATION ? { scope: WebhookEndpointScope.ORGANIZATION } : {}
      }),
    getBasePath: () => '/settings/advanced/webhooks/:webhookEndpointId'
  },
  webhookEventDetails: {
    getPath: ({ webhookEndpointId, webhookEventId, scope }) =>
      getUrlWithQueryParams({
        path: `/settings/advanced/webhooks/${webhookEndpointId}/events/${webhookEventId}`,
        queryParams: scope === WebhookEndpointScope.ORGANIZATION ? { scope: WebhookEndpointScope.ORGANIZATION } : {}
      }),
    getBasePath: () => '/settings/advanced/webhooks/:webhookEndpointId/events/:webhookEventId'
  },
  webhookAttemptDetails: {
    getPath: ({ webhookEndpointId, webhookAttemptId, scope }) =>
      getUrlWithQueryParams({
        path: `/settings/advanced/webhooks/${webhookEndpointId}/attempts/${webhookAttemptId}`,
        queryParams: scope === WebhookEndpointScope.ORGANIZATION ? { scope: WebhookEndpointScope.ORGANIZATION } : {}
      }),
    getBasePath: () => '/settings/advanced/webhooks/:webhookEndpointId/attempts/:webhookAttemptId'
  },
  // Per-department ROUTE pay defaults (route settlements + route solver), NOT
  // the time-tracking clocked-hours feature.
  routePaymentSettings: {
    getPath: () => `/route-payment-settings`,
    getBasePath: () => '/route-payment-settings'
  },
  workerPayrolls: {
    getPath: () => `/worker-payrolls`,
    getBasePath: () => '/worker-payrolls'
  },
  workerPayroll: {
    getPath: (data) => `/worker-payrolls/${data.id}`,
    getBasePath: () => '/worker-payrolls/:id'
  },
  createWorkerPayroll: {
    getPath: () => `/worker-payrolls/create`,
    getBasePath: () => '/worker-payrolls/create'
  },
  workerContractAddress: {
    getPath: (data) => `/worker-contracts/${data.workerContractId}/addresses/${data.addressPairId}`,
    getBasePath: () => '/worker-contracts/:workerContractId/addresses/:addressPairId'
  },
  createWorkerContract: {
    getPath: () => `/worker-contracts/create`,
    getBasePath: () => '/worker-contracts/create'
  },
  createPricingAgreements: {
    getPath: (data) => `/customers/${data.id}/pricing-agreements/create`,
    getBasePath: () => '/customers/:id/pricing-agreements/create'
  },
  editWorkerContract: {
    getPath: ({ workerContractId, ...rest }) =>
      getUrlWithQueryParams({
        path: `/worker-contracts/${workerContractId}`,
        queryParams: rest
      }),
    getBasePath: () => '/worker-contracts/:workerContractId'
  },
  serviceDepartmentArea: {
    getPath: (data) => `/delivery/service-areas/${data.id}`,
    getBasePath: () => '/delivery/service-areas/:id'
  },
  login: {
    getPath: () => '/login',
    getBasePath: () => '/login'
  },
  resetPassword: {
    getPath: () => '/reset-password',
    getBasePath: () => '/reset-password'
  },
  home: {
    getPath: () => '/',
    getBasePath: () => '/'
  },
  workerShifts: {
    getPath: (queryParams = {}) => getUrlWithQueryParams({ path: `/worker-shifts`, queryParams }),
    getBasePath: () => '/worker-shifts'
  },
  workerShiftsOverviewDay: {
    getPath: ({ date, ...queryParams }) =>
      getUrlWithQueryParams({
        path: `/worker-shifts/overview/${date}`,
        queryParams
      }),
    getBasePath: () => '/worker-shifts/overview/:date'
  },
  mobileCoverageCategory: {
    getPath: ({ categoryId, ...queryParams }) =>
      getUrlWithQueryParams({
        path: `/mobile-coverage/categories/${categoryId}`,
        queryParams
      }),
    getBasePath: () => '/mobile-coverage/categories/:categoryId'
  },
  mobileCoverageService: {
    getPath: ({ salesItemId, ...queryParams }) =>
      getUrlWithQueryParams({
        path: `/mobile-coverage/services/${salesItemId}`,
        queryParams
      }),
    getBasePath: () => '/mobile-coverage/services/:salesItemId'
  },
  mobileCoverage: {
    getPath: (queryParams = {}) => getUrlWithQueryParams({ path: '/mobile-coverage', queryParams }),
    getBasePath: () => '/mobile-coverage'
  },
  garageSchedule: {
    getPath: (queryParams) => getUrlWithQueryParams({ path: '/garage-schedule', queryParams }),
    getBasePath: () => '/garage-schedule'
  },
  calendar: {
    getPath: (queryParams) => getUrlWithQueryParams({ path: '/calendar', queryParams }),
    getBasePath: () => '/calendar'
  },
  today: {
    getPath: (queryParams) => getUrlWithQueryParams({ path: '/today', queryParams }),
    getBasePath: () => '/today'
  },
  opsOverview: {
    getPath: () => '/ops',
    getBasePath: () => '/ops'
  },
  shopAvailability: {
    getPath: () => `/shop-availability`,
    getBasePath: () => '/shop-availability'
  },
  operationsCalendar: {
    getPath: () => `/capacity-calendar`,
    getBasePath: () => '/capacity-calendar'
  },
  workerShiftsCreate: {
    getPath: (queryParams = {}) => getUrlWithQueryParams({ path: `/worker-shifts/create`, queryParams }),
    getBasePath: () => '/worker-shifts/create'
  },
  workerShiftDetails: {
    getPath: ({ workerShiftId, ...rest }) =>
      getUrlWithQueryParams({
        path: `/worker-shifts/shift/${workerShiftId}`,
        queryParams: rest
      }),
    getBasePath: () => '/worker-shifts/shift/:workerShiftId'
  },
  bookableTimeDetails: {
    getPath: ({ capacityContributionId }) => `/worker-shifts/${capacityContributionId}`,
    getBasePath: () => '/worker-shifts/:capacityContributionId'
  },
  expectedStaffingDetails: {
    getPath: ({ capacityContributionId, ...rest }) =>
      getUrlWithQueryParams({
        path: `/worker-shifts/expected-staffing/${capacityContributionId}`,
        queryParams: rest
      }),
    getBasePath: () => '/worker-shifts/expected-staffing/:capacityContributionId'
  },
  routePlanning: {
    getPath: (data) =>
      getUrlWithQueryParams({
        path: '/route-planning',
        queryParams: data ?? {}
      }),
    getBasePath: () => '/route-planning'
  },
  routeSettlements: {
    getPath: () => `/route-settlements`,
    getBasePath: () => '/route-settlements'
  },
  routeSettlementDetails: {
    getPath: (data) => `/route-settlements/${data.routeSettlementId}`,
    getBasePath: () => '/route-settlements/:routeSettlementId'
  },
  routeProblemDetails: {
    getPath: (data) => `/route-planning/${data.problemId}`,
    getBasePath: () => '/route-planning/:problemId'
  },
  routeSolutionDetails: {
    getPath: (data) => `/route-planning/${data.problemId}/solutions/${data.solutionId}`,
    getBasePath: () => '/route-planning/:problemId/solutions/:solutionId'
  },
  routeStatus: {
    getPath: ({ routeDetailsId, expandGroups, ...rest } = {}) =>
      getUrlWithQueryParams({
        path: `/route-status`,
        queryParams: {
          ...rest,
          [ROUTE_DETAILS_QUERY_PARAM]: routeDetailsId,
          ...(expandGroups
            ? {
                [ROUTE_STATUS_GROUP_QUERY_PARAMS.inProgress]: true,
                [ROUTE_STATUS_GROUP_QUERY_PARAMS.notStarted]: true,
                [ROUTE_STATUS_GROUP_QUERY_PARAMS.completed]: true
              }
            : {})
        }
      }),
    getBasePath: () => '/route-status'
  },
  routeStatusEdit: {
    getPath: (queryParams = {}) => getUrlWithQueryParams({ path: `/route-status/edit`, queryParams }),
    getBasePath: () => '/route-status/edit'
  },
  route: {
    getPath: ({ id }) => `/routes/${id}`,
    getBasePath: () => '/routes/:id'
  },
  routeMessage: {
    getPath: ({ id, ...queryParams }) => getUrlWithQueryParams({ path: `/routes/${id}/message`, queryParams }),
    getBasePath: () => '/routes/:id/message'
  },
  routeDiscount: {
    getPath: ({ id, ...queryParams }) => getUrlWithQueryParams({ path: `/routes/${id}/discount`, queryParams }),
    getBasePath: () => '/routes/:id/discount'
  },
  bookingUpdateRequests: {
    getPath: () => `/booking-update-requests`,
    getBasePath: () => '/booking-update-requests'
  },
  bookingUpdateRequestCreate: {
    getPath: () => `/booking-update-requests/create`,
    getBasePath: () => '/booking-update-requests/create'
  },
  bookingUpdateRequestDetail: {
    getPath: ({ id }) => `/booking-update-requests/${id}`,
    getBasePath: () => '/booking-update-requests/:id'
  },
  serviceVehicles: {
    getPath: () => '/service-vehicles',
    getBasePath: () => '/service-vehicles'
  },
  orders: {
    getPath: () => `/orders`,
    getBasePath: () => '/orders'
  },
  order: {
    getPath: ({ orderId }) => `/orders/${orderId}`,
    getBasePath: () => '/orders/:orderId'
  },
  refundableOrders: {
    getPath: () => '/orders/refundable',
    getBasePath: () => '/orders/refundable'
  },
  invoices: {
    getPath: () => '/invoices',
    getBasePath: () => '/invoices'
  },
  // Static segment, so React Router ranks it above /invoices/:invoiceId.
  invoiceCreate: {
    getPath: (data) => getUrlWithQueryParams({ path: '/invoices/create', queryParams: data }),
    getBasePath: () => '/invoices/create'
  },
  invoiceDetail: {
    getPath: ({ invoiceId, ...rest }) =>
      getUrlWithQueryParams({
        path: `/invoices/${invoiceId}`,
        queryParams: rest
      }),
    getBasePath: () => '/invoices/:invoiceId'
  },
  refundOrderLines: {
    getPath: (data) => `/orders/${data.orderId}/refund`,
    getBasePath: () => '/orders/:orderId/refund'
  },
  bookingsOverview: {
    getPath: () => `/bookings/overview`,
    getBasePath: () => '/bookings/overview'
  },
  bookingTrends: {
    getPath: () => '/bookings/trends',
    getBasePath: () => '/bookings/trends'
  },
  createTireOfferFromInventory: {
    getPath: () => '/tire-offers/create-from-inventory',
    getBasePath: () => '/tire-offers/create-from-inventory'
  },
  tireOfferNudge: {
    getPath: () => '/tire-offers/nudge',
    getBasePath: () => '/tire-offers/nudge'
  },
  tireSetOffer: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/tire-offers/${id}`,
        queryParams: rest
      }),
    getBasePath: () => `/tire-offers/:id`
  },
  tireOfferLeads: {
    getPath: () => `/tire-offer-leads`,
    getBasePath: () => '/tire-offer-leads'
  },
  tireOfferOverview: {
    getPath: () => `/tire-offers/overview`,
    getBasePath: () => '/tire-offers/overview'
  },
  tireOfferLead: {
    getPath: (data) => `/tire-offer-leads/car/${data.carId}/user-group/${data.userGroupId}/season/${data.season}`,
    getBasePath: () => '/tire-offer-leads/car/:carId/user-group/:userGroupId/season/:season'
  },
  tireSetOffers: {
    getPath: (queryParams = {}) => getUrlWithQueryParams({ path: '/tire-offers', queryParams }),
    getBasePath: () => '/tire-offers'
  },
  tireSupplierOrders: {
    getPath: () => `/tire-offers/supplier-orders`,
    getBasePath: () => '/tire-offers/supplier-orders'
  },
  tireInventory: {
    getPath: () => `/tire-inventory`,
    getBasePath: () => '/tire-inventory'
  },
  createTireInventory: {
    getPath: () => `/tire-inventory/create`,
    getBasePath: () => '/tire-inventory/create'
  },
  tireInventoryConfiguration: {
    getPath: () => `/tire-inventory-configuration`,
    getBasePath: () => '/tire-inventory-configuration'
  },
  rimInventory: {
    getPath: () => `/rim-inventory`,
    getBasePath: () => '/rim-inventory'
  },
  editTireInventoryConfiguration: {
    getPath: (data) => `/tire-inventory/configuration/${data.brandId}`,
    getBasePath: () => '/tire-inventory/configuration/:brandId'
  },
  notifications: {
    getPath: () => `/notifications`,
    getBasePath: () => '/notifications'
  },
  userGroupMemberDetails: {
    getPath: ({ userGroupId, userId, ...rest }) =>
      getUrlWithQueryParams({
        path: `/customers/${userGroupId}/members/${userId}`,
        queryParams: rest
      }),
    getBasePath: () => '/customers/:userGroupId/members/:userId'
  },
  newBooking: {
    getPath: () => '/bookings/new',
    getBasePath: () => '/bookings/new'
  },
  newBookingUserGroup: {
    getPath: (data) => `/customers/${data.id}/new-booking`,
    getBasePath: () => '/customers/:id/new-booking'
  },
  productsCatalog: {
    getPath: (queryParams) =>
      getUrlWithQueryParams({
        path: `/products-catalog`,
        queryParams
      }),
    getBasePath: () => '/products-catalog'
  },
  createProduct: {
    getPath: () => '/products/create',
    getBasePath: () => '/products/create'
  },
  openingHours: {
    getPath: () => `/opening-hours`,
    getBasePath: () => '/opening-hours'
  },
  deliverySettings: {
    getPath: () => `/delivery-settings`,
    getBasePath: () => '/delivery-settings'
  },
  unableToCompleteReasons: {
    getPath: () => `/unable-to-complete-reasons`,
    getBasePath: () => '/unable-to-complete-reasons'
  },
  salesItemDetails: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/products/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/products/:id'
  },
  salesItemTaskChangelog: {
    getPath: ({ id }) => `/products/${id}/task-changelog`,
    getBasePath: () => '/products/:id/task-changelog'
  },
  tasks: {
    getPath: (queryParams = {}) =>
      getUrlWithQueryParams({
        path: '/tasks',
        queryParams
      }),
    getBasePath: () => '/tasks'
  },
  createTask: {
    getPath: () => `/tasks/create`,
    getBasePath: () => '/tasks/create'
  },
  editTask: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/tasks/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/tasks/:id'
  },
  createTaskTemplate: {
    getPath: () => '/tasks/reusable/create',
    getBasePath: () => '/tasks/reusable/create'
  },
  editTaskTemplate: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/tasks/reusable/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/tasks/reusable/:id'
  },
  ruleSet: {
    getPath: (data) => `/filters/${data.id}`,
    getBasePath: () => '/filters/:id'
  },
  createRuleSet: {
    getPath: () => `/filters/create`,
    getBasePath: () => '/filters/create'
  },
  tags: {
    getPath: () => `/tags`,
    getBasePath: () => '/tags'
  },
  createTag: {
    getPath: () => `/tags/create`,
    getBasePath: () => '/tags/create'
  },
  editTag: {
    getPath: (data) => `/tags/${data.id}`,
    getBasePath: () => '/tags/:id'
  },
  guide: {
    getPath: () => `/guide`,
    getBasePath: () => '/guide'
  },
  allCars: {
    getPath: () => `/all-cars`,
    getBasePath: () => '/all-cars'
  },
  inspectionSessions: {
    getPath: () => '/inspections',
    getBasePath: () => '/inspections'
  },
  inspectionSessionDetail: {
    getPath: (data) => `/inspections/${data.id}`,
    getBasePath: () => '/inspections/:id'
  },
  agent: {
    getPath: () => `/agent`,
    getBasePath: () => '/agent'
  },
  templateLibrary: {
    getPath: () => `/template-library`,
    getBasePath: () => '/template-library'
  },
  bookingFlow: {
    getPath: () => `/products/booking-flow`,
    getBasePath: () => '/products/booking-flow'
  },
  productFilters: {
    getPath: () => `/products/filters`,
    getBasePath: () => '/products/filters'
  },
  productTags: {
    getPath: () => `/products/tags`,
    getBasePath: () => '/products/tags'
  },
  siteGroups: { getPath: () => '/campuses', getBasePath: () => '/campuses' },
  parkingPlaces: { getPath: () => '/campuses/parking-places', getBasePath: () => '/campuses/parking-places' },
  parkingPlaceDetail: {
    getPath: ({ id }) => `/campuses/parking-places/${id}`,
    getBasePath: () => '/campuses/parking-places/:id'
  },
  siteGroupCreate: {
    getPath: () => '/campuses/create',
    getBasePath: () => '/campuses/create'
  },
  siteGroupDetail: {
    getPath: ({ id }) => `/campuses/groups/${id}`,
    getBasePath: () => '/campuses/groups/:id'
  },
  siteCreate: {
    getPath: (data = {}) =>
      getUrlWithQueryParams({
        path: '/campuses/buildings/create',
        queryParams: data
      }),
    getBasePath: () => '/campuses/buildings/create'
  },
  siteDetail: {
    getPath: ({ id }) => `/campuses/buildings/${id}`,
    getBasePath: () => '/campuses/buildings/:id'
  },
  garages: {
    getPath: () => `/garages`,
    getBasePath: () => '/garages'
  },
  createGarage: {
    getPath: (data = {}) => getUrlWithQueryParams({ path: '/garages/create', queryParams: data }),
    getBasePath: () => '/garages/create'
  },
  editGarage: {
    getPath: (data) =>
      getUrlWithQueryParams({
        path: `/garages/${data.id}`,
        queryParams: { tab: data.tab }
      }),
    getBasePath: () => '/garages/:id'
  },
  fleetCars: {
    getPath: (queryParams = {}) =>
      getUrlWithQueryParams({
        path: '/fleet-services/cars',
        queryParams
      }),
    getBasePath: () => '/fleet-services/cars'
  },
  fleetServicesOverview: {
    getPath: () => '/fleet-services/overview',
    getBasePath: () => '/fleet-services/overview'
  },
  fleetServicesPlanning: {
    getPath: () => '/fleet-services/planning',
    getBasePath: () => '/fleet-services/planning'
  },
  fleetServicesCalendar: {
    getPath: () => '/fleet-services/calendar',
    getBasePath: () => '/fleet-services/calendar'
  },
  fleetSchedules: {
    getPath: () => '/fleet-services/schedules',
    getBasePath: () => '/fleet-services/schedules'
  },
  createFleetSchedule: {
    getPath: () => '/fleet-services/schedules/create',
    getBasePath: () => '/fleet-services/schedules/create'
  },
  fleetScheduleDetail: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/fleet-services/schedules/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/fleet-services/schedules/:id'
  },
  fleetScheduleVehicleDetail: {
    getPath: ({ id }) => `/fleet-services/schedule-vehicles/${id}`,
    getBasePath: () => '/fleet-services/schedule-vehicles/:id'
  },
  createFleetBooking: {
    getPath: () => '/fleet-services/bookings/create',
    getBasePath: () => '/fleet-services/bookings/create'
  },
  fleetOrganizations: {
    getPath: () => '/fleet-services/organizations',
    getBasePath: () => '/fleet-services/organizations'
  },
  createFleetOrganization: {
    getPath: () => '/fleet-services/organizations/create',
    getBasePath: () => '/fleet-services/organizations/create'
  },
  fleetEditOrganization: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/fleet-services/organizations/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/fleet-services/organizations/:id'
  },
  fleetDepartments: {
    getPath: () => '/fleet-services/departments',
    getBasePath: () => '/fleet-services/departments'
  },
  fleetEditDepartment: {
    getPath: (data) => `/fleet-services/departments/${data.id}`,
    getBasePath: () => '/fleet-services/departments/:id'
  },
  fleetDepartment: {
    getPath: (data) => `/fleet-services/organizations/${data.organizationId}/departments/${data.departmentId}`,
    getBasePath: () => '/fleet-services/organizations/:organizationId/departments/:departmentId'
  },
  tickets: {
    getPath: () => '/tickets',
    getBasePath: () => '/tickets'
  },
  ticketDetail: {
    getPath: ({ id, ...rest }) => getUrlWithQueryParams({ path: `/tickets/${id}`, queryParams: rest }),
    getBasePath: () => '/tickets/:id'
  },
  emailDetail: {
    getPath: (data) => `/communications/emails/${data.id}`,
    getBasePath: () => '/communications/emails/:id'
  },
  transferOrders: {
    getPath: () => '/tire-hotel/transfer-orders',
    getBasePath: () => '/tire-hotel/transfer-orders'
  },
  transferOrderCreate: {
    getPath: () => '/tire-hotel/transfer-orders/new',
    getBasePath: () => '/tire-hotel/transfer-orders/new'
  },
  transferOrderDetail: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/tire-hotel/transfer-orders/${id}`,
        queryParams: rest.tab ? { tab: rest.tab } : undefined
      }),
    getBasePath: () => '/tire-hotel/transfer-orders/:id'
  },
  transferOrderAddWheelSets: {
    getPath: (data) => `/tire-hotel/transfer-orders/${data.id}/add-wheel-sets`,
    getBasePath: () => '/tire-hotel/transfer-orders/:id/add-wheel-sets'
  },
  transferOrderEmailDetail: {
    getPath: (data) => `/tire-hotel/transfer-orders/${data.transferOrderId}/emails/${data.emailId}`,
    getBasePath: () => '/tire-hotel/transfer-orders/:id/emails/:emailId'
  },
  rehomeCreate: {
    getPath: (data) =>
      getUrlWithQueryParams({
        path: '/tire-hotel/rehomes/new',
        queryParams: data?.fromTireHotelId ? { fromTireHotelId: data.fromTireHotelId } : undefined
      }),
    getBasePath: () => '/tire-hotel/rehomes/new'
  },
  rehomeDetail: {
    getPath: ({ id }) => `/tire-hotel/rehomes/${id}`,
    getBasePath: () => '/tire-hotel/rehomes/:id'
  },
  signageDashboards: {
    getPath: () => '/signage/dashboards',
    getBasePath: () => '/signage/dashboards'
  },
  signageDashboardCreate: {
    getPath: () => '/signage/dashboards/create',
    getBasePath: () => '/signage/dashboards/create'
  },
  signageDashboardDetail: {
    getPath: (data) => `/signage/dashboards/${data.id}`,
    getBasePath: () => '/signage/dashboards/:id'
  },
  signagePlaylists: {
    getPath: () => '/signage/playlists',
    getBasePath: () => '/signage/playlists'
  },
  signagePlaylistCreate: {
    getPath: () => '/signage/playlists/create',
    getBasePath: () => '/signage/playlists/create'
  },
  signagePlaylistDetail: {
    getPath: (data) => `/signage/playlists/${data.id}`,
    getBasePath: () => '/signage/playlists/:id'
  },
  signageMedia: {
    getPath: () => '/signage/media',
    getBasePath: () => '/signage/media'
  },
  signageFleet: {
    getPath: () => '/signage/fleet',
    getBasePath: () => '/signage/fleet'
  },
  signageDeviceDetail: {
    getPath: (data) => `/signage/fleet/${data.id}`,
    getBasePath: () => '/signage/fleet/:id'
  },
  signageFleetActivity: {
    getPath: () => '/signage/fleet/activity',
    getBasePath: () => '/signage/fleet/activity'
  },
  signageGroups: {
    getPath: () => '/signage/groups',
    getBasePath: () => '/signage/groups'
  },
  signageRollouts: {
    getPath: () => '/signage/rollouts',
    getBasePath: () => '/signage/rollouts'
  },
  // Deep-link target for on-device QR codes (same path as admin: /signage/pair?code=)
  signagePair: {
    getPath: (data) => {
      if (data?.code) {
        return `/signage/pair?code=${encodeURIComponent(data.code)}`;
      }
      return '/signage/pair';
    },
    getBasePath: () => '/signage/pair'
  },
  equipmentItems: {
    getPath: () => '/equipment/items',
    getBasePath: () => '/equipment/items'
  },
  equipmentItemCreate: {
    getPath: () => '/equipment/items/create',
    getBasePath: () => '/equipment/items/create'
  },
  equipmentItemDetail: {
    getPath: ({ id }) => `/equipment/items/${id}`,
    getBasePath: () => '/equipment/items/:id'
  },
  equipmentTemplates: {
    getPath: () => '/equipment/templates',
    getBasePath: () => '/equipment/templates'
  },
  equipmentTemplateCreate: {
    getPath: () => '/equipment/templates/create',
    getBasePath: () => '/equipment/templates/create'
  },
  equipmentTemplateDetail: {
    getPath: ({ id }) => `/equipment/templates/${id}`,
    getBasePath: () => '/equipment/templates/:id'
  },
  equipmentKits: {
    getPath: () => '/equipment/kits',
    getBasePath: () => '/equipment/kits'
  },
  equipmentKitCreate: {
    getPath: () => '/equipment/kits/create',
    getBasePath: () => '/equipment/kits/create'
  },
  equipmentKitDetail: {
    getPath: ({ id }) => `/equipment/kits/${id}`,
    getBasePath: () => '/equipment/kits/:id'
  },
  equipmentTransfers: {
    getPath: () => '/equipment/transfers',
    getBasePath: () => '/equipment/transfers'
  },
  equipmentTransferCreate: {
    getPath: () => '/equipment/transfers/create',
    getBasePath: () => '/equipment/transfers/create'
  },
  equipmentTransferDetail: {
    getPath: ({ id }) => `/equipment/transfers/${id}`,
    getBasePath: () => '/equipment/transfers/:id'
  },
  equipmentAudits: {
    getPath: () => '/equipment/audits',
    getBasePath: () => '/equipment/audits'
  },
  equipmentAuditCreate: {
    getPath: () => '/equipment/audits/create',
    getBasePath: () => '/equipment/audits/create'
  },
  equipmentAuditDetail: {
    getPath: ({ id }) => `/equipment/audits/${id}`,
    getBasePath: () => '/equipment/audits/:id'
  },
  equipmentStockAlerts: {
    getPath: () => '/equipment/alerts',
    getBasePath: () => '/equipment/alerts'
  },
  equipmentStockAlertCreate: {
    getPath: () => '/equipment/alerts/create',
    getBasePath: () => '/equipment/alerts/create'
  },
  equipmentStockAlertDetail: {
    getPath: ({ id }) => `/equipment/alerts/${id}`,
    getBasePath: () => '/equipment/alerts/:id'
  },
  equipmentScan: {
    getPath: () => '/equipment/scan',
    getBasePath: () => '/equipment/scan'
  },
  equipmentPurchasePlans: {
    getPath: () => '/equipment/purchase-plans',
    getBasePath: () => '/equipment/purchase-plans'
  },
  equipmentPurchasePlanCreate: {
    getPath: () => '/equipment/purchase-plans/create',
    getBasePath: () => '/equipment/purchase-plans/create'
  },
  equipmentPurchasePlanDetail: {
    getPath: ({ id }) => `/equipment/purchase-plans/${id}`,
    getBasePath: () => '/equipment/purchase-plans/:id'
  },
  equipmentVendors: {
    getPath: () => '/equipment/vendors',
    getBasePath: () => '/equipment/vendors'
  },
  equipmentVendorCreate: {
    getPath: () => '/equipment/vendors/create',
    getBasePath: () => '/equipment/vendors/create'
  },
  equipmentVendorDetail: {
    getPath: ({ id }) => `/equipment/vendors/${id}`,
    getBasePath: () => '/equipment/vendors/:id'
  },
  unlockLocks: {
    getPath: (data) => getUrlWithQueryParams({ path: '/unlock/locks', queryParams: data }),
    getBasePath: () => '/unlock/locks'
  },
  unlockLockDetail: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/unlock/locks/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/unlock/locks/:id'
  },
  unlockAccessEvents: {
    getPath: (data) =>
      getUrlWithQueryParams({
        path: '/unlock/access-events',
        queryParams: data
      }),
    getBasePath: () => '/unlock/access-events'
  },
  wmsWarehouses: {
    getPath: () => '/wms/warehouses',
    getBasePath: () => '/wms/warehouses'
  },
  wmsWarehouseCreate: {
    getPath: (data = {}) =>
      getUrlWithQueryParams({
        path: '/wms/warehouses/create',
        queryParams: data
      }),
    getBasePath: () => '/wms/warehouses/create'
  },
  wmsWarehouseDetail: {
    getPath: ({ id, ...rest }) =>
      getUrlWithQueryParams({
        path: `/wms/warehouses/${id}`,
        queryParams: rest
      }),
    getBasePath: () => '/wms/warehouses/:id'
  },
  wmsLevels: {
    getPath: (data = {}) => getUrlWithQueryParams({ path: '/wms/levels', queryParams: data }),
    getBasePath: () => '/wms/levels'
  },
  wmsLevelDetail: {
    getPath: ({ id }) => `/wms/levels/${id}`,
    getBasePath: () => '/wms/levels/:id'
  },
  wmsReservations: {
    getPath: (data = {}) => getUrlWithQueryParams({ path: '/wms/reservations', queryParams: data }),
    getBasePath: () => '/wms/reservations'
  },
  wmsReservationDetail: {
    getPath: ({ id }) => `/wms/reservations/${id}`,
    getBasePath: () => '/wms/reservations/:id'
  },
  wmsAllocations: {
    getPath: (data = {}) => getUrlWithQueryParams({ path: '/wms/allocations', queryParams: data }),
    getBasePath: () => '/wms/allocations'
  },
  wmsAllocationDetail: {
    getPath: ({ id }) => `/wms/allocations/${id}`,
    getBasePath: () => '/wms/allocations/:id'
  },
  wmsMovements: {
    getPath: (data = {}) => getUrlWithQueryParams({ path: '/wms/movements', queryParams: data }),
    getBasePath: () => '/wms/movements'
  },
  wmsAvailability: {
    getPath: (data) => getUrlWithQueryParams({ path: '/wms/availability', queryParams: data }),
    getBasePath: () => '/wms/availability'
  },
  wmsCycleCounts: {
    getPath: (data = {}) => getUrlWithQueryParams({ path: '/wms/cycle-counts', queryParams: data }),
    getBasePath: () => '/wms/cycle-counts'
  },
  wmsCycleCountCreate: {
    getPath: (data) =>
      getUrlWithQueryParams({
        path: '/wms/cycle-counts/create',
        queryParams: data
      }),
    getBasePath: () => '/wms/cycle-counts/create'
  },
  wmsCycleCountDetail: {
    getPath: ({ id }) => `/wms/cycle-counts/${id}`,
    getBasePath: () => '/wms/cycle-counts/:id'
  },
  wmsStockPolicies: {
    getPath: (data = {}) => getUrlWithQueryParams({ path: '/wms/stock-policies', queryParams: data }),
    getBasePath: () => '/wms/stock-policies'
  },
  wmsOrganizationPolicy: {
    getPath: () => '/wms/organization-policy',
    getBasePath: () => '/wms/organization-policy'
  },
  wmsTransfers: {
    getPath: () => '/wms/transfers',
    getBasePath: () => '/wms/transfers'
  },
  wmsTransferCreate: {
    getPath: (data) =>
      getUrlWithQueryParams({
        path: '/wms/transfers/create',
        queryParams: data
      }),
    getBasePath: () => '/wms/transfers/create'
  },
  wmsTransferDetail: {
    getPath: ({ id }) => `/wms/transfers/${id}`,
    getBasePath: () => '/wms/transfers/:id'
  },
  wmsScan: {
    getPath: (data) => getUrlWithQueryParams({ path: '/wms/scan', queryParams: data }),
    getBasePath: () => '/wms/scan'
  }
});
